import { useState, useEffect } from 'react';
import { KeyWithSecret } from '@optidev/shared-types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { CopyableCell } from '@/components/ui/copyable-cell';
import { Info, Loader2 } from 'lucide-react';
import { auth } from '@/lib/firebase';
import { workspaceKeysApi } from '@/lib/api/workspace-keys';
import toast from 'react-hot-toast';

interface RotateKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  keyId: string;
  currentVersion: number;
  onSuccess: () => void;
}

export default function RotateKeyModal({
  isOpen,
  onClose,
  keyId,
  currentVersion,
  onSuccess,
}: RotateKeyModalProps) {
  const [rotating, setRotating] = useState(false);
  const [rotatedKey, setRotatedKey] = useState<KeyWithSecret | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setRotatedKey(null);
    }
  }, [isOpen]);

  const handleRotate = async () => {
    setRotating(true);
    try {
      const token = await auth.getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const result = await workspaceKeysApi.rotate(token, keyId);
      setRotatedKey(result);
      toast.success('API key rotated successfully');
    } catch (error: any) {
      console.error('Failed to rotate key:', error);
      toast.error(error.response?.data?.message || 'Failed to rotate key');
    } finally {
      setRotating(false);
    }
  };

  const handleClose = () => {
    if (rotatedKey) {
      onSuccess();
    }
    onClose();
  };

  const handleSavedConfirmation = () => {
    onSuccess();
    onClose();
  };

  const newVersion = currentVersion + 1;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] rounded-lg w-full max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>Rotate API Key Secret</DialogTitle>
          <DialogDescription>Generate a new secret for this API key</DialogDescription>
        </DialogHeader>

        {!rotatedKey ? (
          <div className="space-y-4">
            <div className="text-center py-4">
              <p className="text-lg font-medium mb-2">
                v{currentVersion} → v{newVersion}
              </p>
              <p className="text-sm text-muted-foreground">
                This will generate a new secret while keeping the same key ID
              </p>
            </div>

            <Card className="border-blue-500/20 bg-blue-50/50 dark:bg-blue-900/10">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Grace Period</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Old secret (v{currentVersion}) will remain valid for 6 hours after rotation
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={onClose} disabled={rotating}>
                Cancel
              </Button>
              <Button onClick={handleRotate} disabled={rotating}>
                {rotating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Rotating...
                  </>
                ) : (
                  'Confirm Rotation'
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Secret (v{rotatedKey.version})</Label>
              <div className="relative">
                <code className="block text-sm font-mono bg-muted px-3 py-2 pr-12 rounded border break-all">
                  {rotatedKey.secret}
                </code>
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <CopyableCell
                    value={rotatedKey.secret}
                    display=""
                    tooltipLabel="Copy secret"
                    iconSize={16}
                    iconClassName="text-muted-foreground hover:text-foreground"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button onClick={handleSavedConfirmation}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
